import type { Honorary, HonoraryStatus, UpdateHonoraryDto } from '@/types'

const INSTALLMENT_STORAGE_KEY = 'jfinance.installment-statuses'

type InstallmentStorageItem = {
  paidInstallments: number[]
  upfrontPaidValue?: number
}

type InstallmentOverrideMap = Record<string, InstallmentStorageItem | number[]>

export interface HonoraryInstallment {
  id: string
  honoraryId: string
  customerId: string
  processNumber: string
  legalArea: Honorary['legalArea']
  paymentMethod: Honorary['paymentMethod']
  installmentNumber: number
  installmentCount: number
  label: string
  value: number
  paidValue: number
  dueDate: string
  status: HonoraryStatus
}

export interface HonoraryInstallmentSummary {
  totalAmount: number
  totalPaid: number
  totalPending: number
  paidCount: number
  pendingCount: number
  status: HonoraryStatus
  paidInstallments: number[]
}

const roundToCents = (value: number) => Math.round(value * 100)

const fromCents = (valueInCents: number) => valueInCents / 100

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

export const isCreditPaymentMethod = (paymentMethod: string) => normalizeText(paymentMethod) === 'credito'

const parseHonoraryDate = (dateValue: Honorary['date']) => {
  const parsedDate = new Date(dateValue)
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate
}

const addMonths = (baseDate: Date, monthOffset: number) => {
  const year = baseDate.getFullYear()
  const monthIndex = baseDate.getMonth() + monthOffset
  const targetYear = year + Math.floor(monthIndex / 12)
  const targetMonth = ((monthIndex % 12) + 12) % 12
  const targetDay = Math.min(baseDate.getDate(), new Date(targetYear, targetMonth + 1, 0).getDate())

  return new Date(
    targetYear,
    targetMonth,
    targetDay,
    baseDate.getHours(),
    baseDate.getMinutes(),
    baseDate.getSeconds(),
    baseDate.getMilliseconds()
  )
}

const normalizeInstallmentCount = (honorary: Honorary) => {
  if (!isCreditPaymentMethod(honorary.paymentMethod)) {
    return 1
  }

  return Math.max(honorary.installments || 1, 1)
}

const splitAmountInCents = (totalValue: number, installmentCount: number) => {
  const totalInCents = Math.max(roundToCents(totalValue), 0)
  const baseValue = Math.floor(totalInCents / installmentCount)
  const remainder = totalInCents % installmentCount

  return Array.from({ length: installmentCount }, (_, index) => baseValue + (index < remainder ? 1 : 0))
}

const readInstallmentOverrides = (): InstallmentOverrideMap => {
  if (typeof window === 'undefined') {
    return {}
  }

  try {
    const rawValue = window.localStorage.getItem(INSTALLMENT_STORAGE_KEY)
    return rawValue ? (JSON.parse(rawValue) as InstallmentOverrideMap) : {}
  } catch {
    return {}
  }
}

const writeInstallmentOverrides = (overrides: InstallmentOverrideMap) => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(INSTALLMENT_STORAGE_KEY, JSON.stringify(overrides))
}

const normalizeStorageItem = (value: InstallmentStorageItem | number[] | undefined): InstallmentStorageItem => {
  if (Array.isArray(value)) {
    return {
      paidInstallments: value
    }
  }

  return {
    paidInstallments: value?.paidInstallments || [],
    upfrontPaidValue: typeof value?.upfrontPaidValue === 'number' ? value.upfrontPaidValue : undefined
  }
}

const getOverrideInstallments = (honoraryId: string) => {
  const overrides = readInstallmentOverrides()
  const paidInstallments = normalizeStorageItem(overrides[honoraryId]).paidInstallments

  return [...new Set(paidInstallments)]
    .map((installmentNumber) => Number(installmentNumber))
    .filter((installmentNumber) => Number.isFinite(installmentNumber) && installmentNumber > 0)
    .sort((a, b) => a - b)
}

export const setPaidInstallments = (honoraryId: string, paidInstallments: number[]) => {
  const overrides = readInstallmentOverrides()
  const currentItem = normalizeStorageItem(overrides[honoraryId])
  const normalizedInstallments = [...new Set(paidInstallments)]
    .map((installmentNumber) => Number(installmentNumber))
    .filter((installmentNumber) => Number.isFinite(installmentNumber) && installmentNumber > 0)
    .sort((a, b) => a - b)

  overrides[honoraryId] = {
    ...currentItem,
    paidInstallments: normalizedInstallments
  }
  writeInstallmentOverrides(overrides)
}

export const setUpfrontPaidValue = (honoraryId: string, upfrontPaidValue: number) => {
  const overrides = readInstallmentOverrides()
  const currentItem = normalizeStorageItem(overrides[honoraryId])

  overrides[honoraryId] = {
    ...currentItem,
    upfrontPaidValue: Math.max(upfrontPaidValue, 0)
  }
  writeInstallmentOverrides(overrides)
}

export const clearPaidInstallments = (honoraryId: string) => {
  const overrides = readInstallmentOverrides()

  if (!(honoraryId in overrides)) {
    return
  }

  delete overrides[honoraryId]
  writeInstallmentOverrides(overrides)
}

export const getUpfrontPaidValue = (honorary: Honorary) => {
  const overrides = readInstallmentOverrides()
  const storedValue = normalizeStorageItem(overrides[honorary.id]).upfrontPaidValue

  if (typeof storedValue === 'number') {
    return Math.max(Math.min(storedValue, honorary.value), 0)
  }

  if (normalizeInstallmentCount(honorary) > 1 && honorary.status === 'Pendente' && honorary.paidValue > 0) {
    return Math.max(Math.min(honorary.paidValue, honorary.value), 0)
  }

  return 0
}

export const expandHonoraryInstallments = (honorary: Honorary): HonoraryInstallment[] => {
  const installmentCount = normalizeInstallmentCount(honorary)
  const dueDate = parseHonoraryDate(honorary.date)
  const upfrontPaidValue = getUpfrontPaidValue(honorary)
  const financedValue = Math.max(honorary.value - upfrontPaidValue, 0)
  const installmentValues = splitAmountInCents(financedValue, installmentCount)
  const overridePaidInstallments = getOverrideInstallments(honorary.id)
  const hasOverride = overridePaidInstallments.length > 0
  const paidAfterUpfrontValue = Math.max(honorary.paidValue - upfrontPaidValue, 0)
  const backendPaidValue = honorary.status === 'Recebido'
    ? Math.max(paidAfterUpfrontValue, financedValue)
    : paidAfterUpfrontValue
  let remainingPaidInCents = roundToCents(backendPaidValue)

  return installmentValues.map((installmentValueInCents, index) => {
    const installmentNumber = index + 1
    const installmentValue = fromCents(installmentValueInCents)
    let paidValueInCents = 0

    if (hasOverride) {
      paidValueInCents = overridePaidInstallments.includes(installmentNumber) ? installmentValueInCents : 0
    } else {
      paidValueInCents = Math.max(Math.min(remainingPaidInCents, installmentValueInCents), 0)
      remainingPaidInCents -= paidValueInCents
    }

    return {
      id: `${honorary.id}-${installmentNumber}`,
      honoraryId: honorary.id,
      customerId: honorary.customerId,
      processNumber: honorary.processNumber,
      legalArea: honorary.legalArea,
      paymentMethod: honorary.paymentMethod,
      installmentNumber,
      installmentCount,
      label: `${installmentNumber}/${installmentCount}`,
      value: installmentValue,
      paidValue: fromCents(paidValueInCents),
      dueDate: addMonths(dueDate, index).toISOString(),
      status: paidValueInCents >= installmentValueInCents ? 'Recebido' : 'Pendente'
    }
  })
}

export const summarizeHonoraryInstallments = (honorary: Honorary) => {
  const installments = expandHonoraryInstallments(honorary)
  const summary: HonoraryInstallmentSummary = {
    totalAmount: 0,
    totalPaid: 0,
    totalPending: 0,
    paidCount: 0,
    pendingCount: 0,
    status: 'Pendente',
    paidInstallments: []
  }

  installments.forEach((installment) => {
    summary.totalAmount += installment.value
    summary.totalPaid += installment.paidValue
    summary.totalPending += Math.max(installment.value - installment.paidValue, 0)

    if (installment.status === 'Recebido') {
      summary.paidCount += 1
      summary.paidInstallments.push(installment.installmentNumber)
      return
    }

    summary.pendingCount += 1
  })

  summary.status = summary.pendingCount === 0 ? 'Recebido' : 'Pendente'

  return {
    installments,
    summary
  }
}

export const buildHonoraryInstallmentUpdate = (honorary: Honorary, paidInstallments: number[]): UpdateHonoraryDto => {
  const installmentCount = normalizeInstallmentCount(honorary)
  const upfrontPaidValue = getUpfrontPaidValue(honorary)
  const financedValue = Math.max(honorary.value - upfrontPaidValue, 0)
  const normalizedPaidInstallments = [...new Set(paidInstallments)]
    .map((installmentNumber) => Number(installmentNumber))
    .filter((installmentNumber) => installmentNumber >= 1 && installmentNumber <= installmentCount)
    .sort((a, b) => a - b)
  const installmentValues = splitAmountInCents(financedValue, installmentCount)
  const totalPaidInCents = normalizedPaidInstallments.reduce((accumulator, installmentNumber) => {
    return accumulator + installmentValues[installmentNumber - 1]
  }, 0)
  const paidValue = upfrontPaidValue + fromCents(totalPaidInCents)

  return {
    paidValue,
    status: normalizedPaidInstallments.length === installmentCount ? 'Recebido' : 'Pendente'
  }
}
